/**
 * Invite and promo codes. A code from an invite link (`/?ref=CODE`) or typed on the sign-in page
 * is kept in this browser until the next sign-in, which sends it along (see api/auth.ts): it
 * survives the trip to Telegram and back, and visits to other pages first.
 */
import { request, send } from './http';

export interface PromoInfo {
  code: string;
  /** What the code adds to the first top-up: bonus_percent of it, at most bonus_cap_usd. */
  bonus_percent: string;
  bonus_cap_usd: string;
}

export interface PromoState extends PromoInfo {
  /** The bonus was added to a top-up already. */
  bonus_used: boolean;
}

export interface Reward {
  amount_usd: string;
  topup_usd: string | null;
  created_at: string;
}

export interface Invites extends PromoInfo {
  /** The inviter's share of each of a friend's top-ups. */
  reward_percent: string;
  invited: number;
  paid: number;
  earned_usd: string;
  rewards: Reward[];
}

export const checkPromoCode = (code: string) =>
  request<PromoInfo>(`/api/promo-codes/${encodeURIComponent(code.trim())}`);

export const fetchInvites = () => request<Invites>('/api/referrals');

export const enterPromoCode = (code: string) =>
  request<PromoState>('/api/account/promo-code', send('POST', { code: code.trim() }));

export interface PayoutMethod {
  currency: string;
  network: string;
  /** The network as people know it: "Solana", "Tron (TRC20)". */
  name: string;
}

export interface Withdrawal {
  id: string;
  amount_usd: string;
  currency: string;
  network: string;
  address: string;
  /** pending | paid | rejected */
  status: string;
  txid: string | null;
  /** Why it was rejected. */
  note: string | null;
  created_at: string;
  decided_at: string | null;
}

export interface Withdrawals {
  available_usd: string;
  min_usd: string;
  methods: PayoutMethod[];
  items: Withdrawal[];
}

export const fetchWithdrawals = () => request<Withdrawals>('/api/withdrawals');

export const requestWithdrawal = (body: {
  currency: string;
  network: string;
  address: string;
  amount_usd: string;
}) => request<Withdrawals>('/api/withdrawals', send('POST', body));

const STORAGE_KEY = 'lcr.promo';
const CODE = /^[A-Za-z0-9][A-Za-z0-9-]{2,31}$/;
const REF_PARAM = 'ref';

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null; // storage disabled
  }
}

export function storedPromoCode(): string | null {
  const code = storage()?.getItem(STORAGE_KEY) ?? null;
  return code !== null && CODE.test(code) ? code : null;
}

export function rememberPromoCode(code: string): void {
  const value = code.trim().toUpperCase();
  if (CODE.test(value)) storage()?.setItem(STORAGE_KEY, value);
}

export function forgetPromoCode(): void {
  storage()?.removeItem(STORAGE_KEY);
}

/** Keeps the code of an invite link and takes it off the address, so it isn't shared further. */
export function takePromoCodeFromUrl(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const code = url.searchParams.get(REF_PARAM);
  if (code === null) return;
  rememberPromoCode(code);
  url.searchParams.delete(REF_PARAM);
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
}

export function inviteLink(code: string): string {
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  return `${origin}/?${REF_PARAM}=${encodeURIComponent(code)}`;
}
